import React, { useMemo } from "react";

import { Stack } from "@mui/system";
import Decimal from "decimal.js";

import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import { Security } from "@fartherfinance/frontend/api/SecurityMaster/requests/Types";
import { SecurityId } from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { BorderedRow } from "@src/sharedComponents/List/BorderedList";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import displayTitleFromSecurity from "@src/utils/displayTitleFromSecurity";

import SubtleSpan from "./SubtleSpan";

interface Props {
  securityId: SecurityId;
  weight: Decimal;
}

const SecurityRow = ({ securityId, weight }: Props) => {
  const auth = useRequestAuth();
  const advisorAuth = useAdvisorRequestAuth();

  const { data, hasError, isLoading } = useSecuritiesBySecurityIds(
    [securityId],
    auth ?? advisorAuth
  );

  const weightAsString: string = `${weight.mul(100).toFixed(2)}%`;

  const firstResult = useMemo((): Security | undefined => {
    return data?.[0];
  }, [data]);

  if (hasError || isLoading || firstResult === undefined) {
    return (
      <BorderedRow
        left={
          <Stack direction="row">
            <Skeleton width={30} /> <Skeleton width={100} />
          </Stack>
        }
        right={weightAsString}
      />
    );
  }

  const identifierToShow = displayTitleFromSecurity(firstResult);

  const description = firstResult.description ?? firstResult.name ?? "N/A";

  const renderDescription = identifierToShow !== description;

  return (
    <BorderedRow
      left={
        <div>
          <span>{identifierToShow}</span>{" "}
          {renderDescription ? (
            <SubtleSpan style={{ paddingLeft: 6 }}>{description}</SubtleSpan>
          ) : null}
        </div>
      }
      right={weightAsString}
    />
  );
};

export default SecurityRow;
